import { getIsVenue, getSupplier } from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useTranslation } from 'next-i18next';
import { memo, useCallback, useMemo, useState, MouseEvent, PropsWithChildren } from 'react';
import SignupLink from '@bridebook/web/app-shared/components/signup-link/signup-link';
import componentStyles from '@bridebook/web/app-shared/components/supplier/toggle-enquiry-form-button/toggle-enquiry-form-button.style';
import { useIsAuthenticated } from '@bridebook/web/lib/auth/hooks/use-is-authenticated';
import useSignAnalytics from '@bridebook/web/app-basic/lib/hooks/use-sign-analytics';
import dynamic from 'next/dynamic';
import { useSignupRedirectProps } from '@bridebook/web/app-basic/components/supplier/hooks/use-signup-redirect-props';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@bridebook/aura';

// Code-split the signup modal out of the basic page bundle; it only loads when a
// logged-out user opens it (which also defers the reCAPTCHA enterprise script).
const SignupModal = dynamic(() => import('@bridebook/web/components/auth/signup-modal'), {
  ssr: false,
});

interface IProps extends PropsWithChildren {
  contactMethod?: string;
  genericTitle?: boolean;
  asChild?: boolean;
}

const ToggleEnquiryFormWithSignup = ({
  contactMethod,
  genericTitle,
  children,
  asChild = false,
}: IProps) => {
  const { t } = useTranslation();
  const isVenue = useSelector(getIsVenue);
  const supplier = useSelector(getSupplier);
  const { isAuthenticated, isLoading } = useIsAuthenticated();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const signupLinkProps = useSignupRedirectProps();

  const clickedSection = useMemo(() => {
    if (contactMethod === 'supplierProfile_stickyBottomBar') return 'stickyBottomBar';

    if (
      contactMethod === 'supplierProfile_topButton' ||
      contactMethod === 'supplierProfile_topButtonDownloadable'
    )
      return 'enquiryTopButton';

    return 'enquiryDetails';
  }, [contactMethod]);

  const enquiryCTAText = useMemo(
    () => (isVenue ? t('common:cta.requestBrochure') : t('common:enquiryCTA.nonVenue')),
    [isVenue, t],
  );

  const styles = useMemo(() => componentStyles(), []);

  const analyticsHandler = useSignAnalytics('supplierProfile', clickedSection, enquiryCTAText);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (isLoading) return;

      if (!isAuthenticated) {
        e.preventDefault();
        e.stopPropagation();
        analyticsHandler();
        setShowSignupModal(true);
        return;
      }

      // User is authenticated - let SignupLink handle the redirect/action
    },
    [isAuthenticated, isLoading, analyticsHandler],
  );

  const handleCloseModal = useCallback(() => {
    setShowSignupModal(false);
  }, []);

  if (!supplier) return null;

  // If user is authenticated, use normal SignupLink behavior
  if ((isAuthenticated && !showSignupModal) || !isVenue) {
    return (
      <SignupLink
        isLoggedIn={!!isAuthenticated}
        style={styles.wrapper}
        clickedLocation="supplierProfile"
        clickedSection={clickedSection}
        cta={enquiryCTAText}
        {...signupLinkProps}>
        {children}
      </SignupLink>
    );
  }

  const Comp = asChild ? Slot : 'div';

  // User is not authenticated and venues only, show modal on click
  return (
    <>
      <Comp onClick={handleClick} className={cn(!asChild && 'cursor-pointer')}>
        {children}
      </Comp>
      <SignupModal
        title={
          genericTitle ? undefined : t('signup:signupModal.mainTitle', { name: supplier.name })
        }
        descriptions={[
          t('signup:signupModal.description1'),
          t('signup:signupModal.description2'),
          t('signup:signupModal.description3'),
        ]}
        isOpen={showSignupModal}
        signupLinkProps={signupLinkProps}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default memo(ToggleEnquiryFormWithSignup);
